import React from 'react';
import { useDatabases } from '@/modules/databases/hooks/useDatabases';
import type { DatabaseResponse } from '@troia-v3/types';
import { DatabaseStatus } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
import { SectionCard } from './SectionCard';
interface DatabasesTabProps {
  selectedDatabases: string[];
  onChange: (databases: string[]) => void;
}

export const DatabasesTab: React.FC<DatabasesTabProps> = ({
  selectedDatabases,
  onChange
}) => {
  // Buscar databases disponíveis via API
  const { data: databasesData, isLoading } = useDatabases({
    filters: { status: DatabaseStatus.ACTIVE },
    limit: 100  // Buscar até 100 databases ativas
  });

  // Extrair lista de databases (ListResponse tem items[])
  const availableDatabases = databasesData?.items || [];

  const handleToggleDatabase = (databaseId: string) => {
    if (selectedDatabases.includes(databaseId)) {
      onChange(selectedDatabases.filter(id => id !== databaseId));
    } else {
      onChange([...selectedDatabases, databaseId]);
    }
  };

  return (
    <div className="space-y-4">
      {/* Info Box */}
      <div
        className="p-4 rounded-lg border"
        style={{
          backgroundColor: 'var(--theme-info-background)',
          borderColor: 'var(--theme-info-color)',
        }}
      >
        <p className="text-sm" style={{ color: 'var(--theme-info-color)' }}>
          <strong>Importante:</strong> Selecione as bases de dados que este agente poderá consultar durante as conversas.
          Os dados serão disponibilizados automaticamente como contexto para as respostas do agente.
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--theme-primary)' }}></div>
          <p className="ml-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            Carregando bases de dados...
          </p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && availableDatabases.length === 0 && (
        <div
          className="p-8 text-center rounded-lg border-2 border-dashed"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          <Icon name="database" className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.4 }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-card-text)' }}>
            Nenhuma base de dados cadastrada
          </p>
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Configure bases de dados no módulo "Databases" para poder selecioná-las aqui.
          </p>
        </div>
      )}

      {/* Database Selection */}
      {!isLoading && availableDatabases.length > 0 && (
        <SectionCard
          icon="database"
          title="Bancos de Dados Disponíveis"
          subtitle="O agente consulta as bases selecionadas como contexto nas respostas"
        >
          <div className="space-y-3">
            {availableDatabases.map((database: DatabaseResponse) => (
            <label
              key={database.id}
              className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all"
              style={{
                borderColor: selectedDatabases.includes(database.id)
                  ? 'var(--theme-primary)'
                  : 'var(--theme-card-border)',
                backgroundColor: selectedDatabases.includes(database.id)
                  ? 'var(--theme-primary-background)'
                  : 'var(--theme-card-background)',
              }}
              onMouseEnter={(e) => {
                if (!selectedDatabases.includes(database.id)) {
                  e.currentTarget.style.borderColor = 'var(--theme-muted-text)';
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedDatabases.includes(database.id)) {
                  e.currentTarget.style.borderColor = 'var(--theme-card-border)';
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedDatabases.includes(database.id)}
                onChange={() => handleToggleDatabase(database.id)}
                className="mt-1 w-4 h-4"
                style={{ accentColor: 'var(--theme-primary)' }}
                disabled={database.status !== 'active'}
              />

              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: database.status === 'active'
                    ? 'var(--theme-primary-background)'
                    : 'var(--theme-muted-text)',
                  color: database.status === 'active'
                    ? 'var(--theme-primary)'
                    : 'var(--theme-card-background)',
                }}
              >
                <Icon name="database" className="w-5 h-5" />
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p
                    className="text-sm font-medium"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {database.name}
                  </p>
                  {database.status !== 'active' && (
                    <span
                      className="text-xs px-2 py-0.5 rounded"
                      style={{
                        backgroundColor: 'var(--theme-warning-background)',
                        color: 'var(--theme-warning-color)',
                      }}
                    >
                      Inativa
                    </span>
                  )}
                </div>
                {database.description && (
                  <p
                    className="text-xs mt-1"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {database.description}
                  </p>
                )}
              </div>
            </label>
          ))}
          </div>
        </SectionCard>
      )}

      {/* Selected Count */}
      <div
        className="p-3 rounded-lg"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          border: '1px solid var(--theme-card-border)',
        }}
      >
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          <strong style={{ color: 'var(--theme-card-text)' }}>
            {selectedDatabases.length}
          </strong>{' '}
          base(s) de dados selecionada(s)
        </p>
      </div>
    </div>
  );
};
